import { Alert, Badge, Card, Code, Group, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import { getAdminFlags, type ServiceFlags } from "@/api/portal";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * Admin UI — deployed feature-flag panel (phase2-admin-ui backlog #3).
 *
 * Shows the boolean `SR_*_ENABLED` flags each service was DEPLOYED with, so staff
 * can see what is on per service without a redeploy or log-diving. Read-only — the
 * flags are set by the deployment environment, not editable here. The BFF fetches
 * each service's auth-exempt `/flags`; a service it cannot reach is shown as
 * "Unreachable" (its own flags are still readable), never failing the whole panel.
 */
export function AdminFlagsPage(): JSX.Element {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["admin-flags"],
    queryFn: getAdminFlags,
    retry: false,
    refetchInterval: 60_000,
  });

  return (
    <AppLayout breadcrumbs={[{ label: t("admin.flags_title") }]}>
      <Title order={1}>{t("admin.flags_title")}</Title>
      <Text c="dimmed" mt="xs" mb="md">
        {t("admin.flags_subtitle")}
      </Text>

      {isPending ? (
        <TableSkeleton label={t("admin.flags_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("admin.flags_unavailable")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : (
        <Stack>
          {data.services.map((svc) => (
            <ServiceCard key={svc.service} service={svc} />
          ))}
        </Stack>
      )}
    </AppLayout>
  );
}

function ServiceCard({ service }: { service: ServiceFlags }): JSX.Element {
  const entries = Object.entries(service.flags);
  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Title order={3}>{service.service}</Title>
        {!service.reachable && (
          <Badge color="gray" variant="light">
            {t("admin.flags_unreachable")}
          </Badge>
        )}
      </Group>
      {!service.reachable ? (
        <Text size="sm" c="dimmed">
          {t("admin.flags_unreachable")}
        </Text>
      ) : entries.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("admin.flags_none")}
        </Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("admin.flags_col_flag")}</Table.Th>
              <Table.Th>{t("admin.flags_col_state")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {entries.map(([name, on]) => (
              <Table.Tr key={name}>
                <Table.Td>
                  <Code>{name}</Code>
                </Table.Td>
                <Table.Td>
                  <Badge color={on ? "green" : "gray"} variant="light">
                    {on ? t("admin.flags_on") : t("admin.flags_off")}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}
